import React, { useCallback, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import { toast } from 'react-toastify';
import PasswordStrengthIndicator from '../../../../components/common/PasswordStrengthIndicator';
import { easyWebinarLogoLight } from '../../../../assets/images';
import { AI_AUTH_COLORS } from '../constants';
import AuthService from '../../../../services/auth.service';
import { signupWithoutPayment } from '../../../../services/signupWithoutPayment.service';
import { Applications } from '../../../../types/enums';
import {
  hasCompanyName,
  SIGNUP_WITHOUT_PAYMENT_FREE_TRIAL_PLAN_ID,
} from '../../../../types/signupWithoutPayment';  
import { ChevronRight } from 'lucide-react';

function getBaseSubdomainFromEmail(email: string): string {
  const part = (email.split('@')[0] ?? '').toLowerCase().trim();
  return part.replace(/\s+/g, '').replace(/[^a-z0-9-]/g, '') || 'user';
}

export default function TryAiFlowCreatePasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editableEmail, setEditableEmail] = useState('');

  const email = useMemo(() => searchParams.get('email')?.trim() || '', [searchParams]);
  const trimmedPassword = useMemo(() => password.trim(), [password]);
  const isContinueDisabled = !email || trimmedPassword.length < 6;

  const handlePasswordChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPassword(event.target.value);
    },
    []
  );

  const handleTogglePassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handleStartEditEmail = useCallback(() => {
    setEditableEmail(email);
    setIsEditingEmail(true);
  }, [email]);

  const handleSaveEmail = useCallback(() => {
    const newEmail = editableEmail.trim().toLowerCase();
    if (!newEmail) return;
    const params = new URLSearchParams(searchParams);
    params.set('email', newEmail);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    setIsEditingEmail(false);
  }, [editableEmail, searchParams, navigate, location.pathname]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (isContinueDisabled || isLoading || isEditingEmail) return;

      const emailLower = email.trim().toLowerCase();
      if (!emailLower || !trimmedPassword) return;

      setIsLoading(true);
      try {
        const emailCheck = await AuthService.checkEmailExists(emailLower);
        const data = emailCheck?.data as { exists?: boolean } | undefined;
        if (data?.exists) {
          toast.error('An account with this email already exists. Please log in or use a different email.');
          return;
        }

        const baseSubdomain = getBaseSubdomainFromEmail(emailLower);
        const { subdomain } = await AuthService.generateSubdomain(
          baseSubdomain,
          Applications.EASYWEBINAR
        );
        const check = await AuthService.checkSubDomainAvailability(
          subdomain ?? baseSubdomain,
          Applications.EASYWEBINAR
        );
        const finalSubdomain = check.available
          ? (subdomain ?? baseSubdomain)
          : (check.suggestions?.[0] ?? subdomain ?? baseSubdomain);

        const response = await signupWithoutPayment({
          email: emailLower,
          password: trimmedPassword,
          firstName: emailLower.split('@')[0] || '',
          phoneNumber: '',
          lastName: emailLower.split('@')[1]?.split('.')[0] || '',
          planId: SIGNUP_WITHOUT_PAYMENT_FREE_TRIAL_PLAN_ID,
          subdomain: finalSubdomain,
        });

        const companyId = response.companyId ?? '';
        const companyName = hasCompanyName(response) ? response.companyName ?? '' : '';

        toast.success('Account created. Please check your email for the verification code.');
        navigate(`/try-ai-flow/auth/email-verification?email=${encodeURIComponent(emailLower)}`, {
          state: {
            email: emailLower,
            password: trimmedPassword,
            companyId,
            companyName,
          },
        });
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Signup failed. Please try again.'
        );
      } finally {
        setIsLoading(false);
      }
    },
    [email, trimmedPassword, isContinueDisabled, isLoading, navigate]
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        bgcolor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Link to="/try-ai-flow" style={{ display: 'inline-flex', alignItems: 'center' }}>
          <img
            src={easyWebinarLogoLight}
            alt="EasyWebinar"
            style={{ display: 'block', height: 30, width: 'auto' }}
          />
        </Link>
        <Button
          component={Link}
          to="/try-ai-flow/auth/login"
          variant="contained"
          endIcon={<ChevronRight size={16} />}
          sx={{
            textTransform: 'none',
            borderRadius: '999px',
            px: 1.9,
            py: 0.7,
            fontSize: '0.85rem',
            fontWeight: 600,
            color: '#111827',
            bgcolor: '#FFFFFF',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#F9FAFB',
              boxShadow: 'none',
            },
          }}
        >
          Login
        </Button>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: 4,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          <Typography
            component="h1"
            sx={{
              textAlign: 'center',
              color: '#111827',
              fontWeight: 600,
              fontSize: { xs: '1.8rem', sm: '2.2rem' },
              letterSpacing: '-0.01em',
              mb: 0.7,
            }}
          >
            Create a password
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              color: '#6B7280',
              fontSize: '0.92rem',
              lineHeight: 1.45,
              mb: 2.5,
            }}
          >
            Use this password to sign in to your EasyWebinar account and manage your webinars.
          </Typography>

          <Box component="form" onSubmit={handleSubmit} autoComplete="off">
            <TextField
              fullWidth
              value={isEditingEmail ? editableEmail : email}
              onChange={isEditingEmail ? (e) => setEditableEmail(e.target.value) : undefined}
              placeholder="Email address"
              variant="outlined"
              size="medium"
              disabled={!isEditingEmail}
              inputProps={{ autoComplete: 'off' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutlineIcon sx={{ fontSize: 17, color: '#9CA3AF' }} />
                  </InputAdornment>
                ),
                endAdornment: isEditingEmail ? (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={handleSaveEmail}
                      onMouseDown={(e) => e.preventDefault()}
                      sx={{ color: '#9CA3AF' }}
                      aria-label="Save email"
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={handleStartEditEmail}
                      onMouseDown={(e) => e.preventDefault()}
                      sx={{ color: '#9CA3AF' }}
                      aria-label="Edit email"
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: '#FFFFFF',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&.Mui-disabled': { bgcolor: '#FFFFFF' },
                  '&:hover fieldset': { borderColor: isEditingEmail ? '#D1D5DB' : undefined },
                  '&.Mui-focused fieldset': { borderColor: isEditingEmail ? AI_AUTH_COLORS.primaryBorder : undefined },
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: '#374151',
                  color: '#374151',
                  fontSize: '0.88rem',
                },
                '& input': {
                  fontSize: '0.88rem',
                },
                mb: 1.25,
              }}
            />

            <TextField
              fullWidth
              value={password}
              onChange={handlePasswordChange}
              placeholder="Password"
              variant="outlined"
              size="medium"
              type={showPassword ? 'text' : 'password'}
              inputProps={{ autoComplete: 'new-password' }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={handleTogglePassword}
                      onMouseDown={(event) => event.preventDefault()}
                      sx={{ color: '#9CA3AF' }}
                    >
                      {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: '#FFFFFF',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#D1D5DB' },
                  '&.Mui-focused fieldset': { borderColor: AI_AUTH_COLORS.primaryBorder },
                },
                '& input': {
                  fontSize: '0.88rem',
                },
                mb: 1,
              }}
            />
            <PasswordStrengthIndicator password={password} transparent compact />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={isContinueDisabled || isLoading || isEditingEmail}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                py: 1.05,
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#FFFFFF',
                bgcolor: AI_AUTH_COLORS.primaryMain,
                boxShadow: 'none',
                mt: 2,
                '&:hover': {
                  bgcolor: AI_AUTH_COLORS.primaryHover,
                  boxShadow: 'none',
                },
                '&.Mui-disabled': {
                  bgcolor: AI_AUTH_COLORS.primaryDisabled,
                  color: '#FFFFFF',
                },
              }}
            >
              Continue
            </Button>
          </Box>

          <Typography
            sx={{
              mt: 6,
              textAlign: 'center',
              color: '#6B7280',
              fontSize: '0.78rem',
              lineHeight: 1.6,
            }}
          >
            By creating an account, you agree to our{' '}
            <Typography
              component="a"
              href="https://easywebinar.com/terms-conditions/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline',
                color: '#111827',
                textDecoration: 'none',
                fontSize: 'inherit',
                fontWeight: 'inherit',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Terms of Service
            </Typography>{' '}
            and{' '}
            <Typography
              component="a"
              href="https://easywebinar.com/privacy/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline',
                fontSize: 'inherit',
                fontWeight: 'inherit',
                color: '#111827',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Privacy Policy
            </Typography>
            .
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
